'use client';

import React, { useState, useEffect } from 'react';
import FilterBar from './FilterBar';
import CaseStudyCard from './CaseStudyCard';
import CaseStudyModal from './CaseStudyModal';
import ROICalculator from './ROICalculator';

interface Metric {
  label: string;
  value: string;
  improvement: string;
}

interface TimelineItem {
  phase: string;
  duration: string;
  activities: string[];
}

interface CaseStudy {
  id: number;
  company: string;
  industry: string;
  companySize: string;
  useCase: string;
  image: string;
  alt: string;
  challenge: string;
  solution: string;
  timeline: string;
  metrics: Metric[];
  executiveQuote: string;
  executiveName: string;
  executiveTitle: string;
  detailedChallenge: string;
  detailedSolution: string;
  implementationTimeline: TimelineItem[];
  riskMitigation: string[];
  keyTakeaways: string[];
}

const ResultsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedCompanySize, setSelectedCompanySize] = useState('all');
  const [selectedUseCase, setSelectedUseCase] = useState('all');
  const [selectedOutcome, setSelectedOutcome] = useState('all');
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<CaseStudy | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const caseStudies: CaseStudy[] = [
  {
    id: 1,
    company: "Australian Financial Group",
    industry: "Financial Services",
    companySize: "Enterprise (5,000+ employees)",
    useCase: "Customer Service Automation",
    image: "https://images.unsplash.com/photo-1721240608299-cdf5ee26c235",
    alt: "Modern glass office building with blue sky reflecting corporate financial services environment",
    challenge: "Manual customer inquiry processing causing 48-hour response times and compliance risks",
    solution: "Implemented AIDoo Assist with Sferal.ai orchestration for automated inquiry routing and response",
    timeline: "6 months",
    metrics: [
    { label: "Response Time Reduction", value: "87%", improvement: "From 48hrs to 6hrs" },
    { label: "Cost Savings", value: "$2.4M", improvement: "Annual operational savings" },
    { label: "Customer Satisfaction", value: "+42%", improvement: "CSAT score increase" },
    { label: "Compliance Incidents", value: "0", improvement: "Zero breaches post-implementation" }],

    executiveQuote: "Mindshift AI didn't just implement technology—they transformed our entire customer service operation while maintaining complete regulatory compliance. The ROI exceeded our projections by 40%.",
    executiveName: "Sarah Mitchell",
    executiveTitle: "Chief Operations Officer",
    detailedChallenge: "Australian Financial Group faced mounting pressure from regulators regarding customer inquiry response times while simultaneously dealing with a 300% increase in digital banking queries. Their legacy systems couldn't scale, and ad-hoc AI tools created compliance gaps. Manual processing was costing $4.2M annually with increasing error rates threatening their regulatory standing.",
    detailedSolution: "Mindshift AI implemented a governed AI orchestration platform using Sferal.ai to coordinate AIDoo Assist across all customer touchpoints. The solution included automated inquiry classification, intelligent routing, compliance checking at every step, and seamless human handoff for complex cases. All AI decisions were logged and auditable, ensuring complete regulatory transparency.",
    implementationTimeline: [
    {
      phase: "Discovery & Assessment",
      duration: "4 weeks",
      activities: [
      "Compliance requirement mapping",
      "Current process documentation",
      "Risk assessment and mitigation planning",
      "Stakeholder alignment workshops"]

    },
    {
      phase: "Platform Configuration",
      duration: "8 weeks",
      activities: [
      "Sferal.ai orchestration setup",
      "AIDoo Assist integration",
      "Compliance rule engine configuration",
      "Security and access control implementation"]

    },
    {
      phase: "Pilot Deployment",
      duration: "6 weeks",
      activities: [
      "Limited rollout to 20% of inquiries",
      "Performance monitoring and optimization",
      "Staff training and change management",
      "Compliance validation and audit preparation"]

    },
    {
      phase: "Full Production",
      duration: "6 weeks",
      activities: [
      "Phased rollout to all channels",
      "Continuous monitoring and refinement",
      "Knowledge base expansion",
      "Ongoing staff enablement"]

    }],

    riskMitigation: [
    "100% compliance maintained through automated regulatory checks at every interaction point",
    "Zero data breaches with enterprise-grade security protocols and audit trails",
    "Seamless human escalation preventing AI overreach in complex scenarios",
    "Complete transparency with explainable AI decisions for regulatory reporting"],

    keyTakeaways: [
    "Governed AI orchestration enables rapid scaling without compromising compliance",
    "Proper change management is critical—staff became AI advocates rather than resistors",
    "Measurable ROI achieved within 8 months, exceeding initial projections",
    "Continuous monitoring and optimization drives ongoing improvement beyond initial deployment"]

  },
  {
    id: 2,
    company: "HealthCare NZ",
    industry: "Healthcare",
    companySize: "Mid-Market (1,200 employees)",
    useCase: "Clinical Documentation",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_122893eee-1768046466635.png",
    alt: "Modern hospital corridor with medical staff in white coats walking through bright clean healthcare facility",
    challenge: "Clinicians spending 40% of time on documentation instead of patient care",
    solution: "Deployed AIDoo Analyzer with voice-to-text and automated clinical note generation",
    timeline: "5 months",
    metrics: [
    { label: "Documentation Time", value: "65%", improvement: "Reduction in admin time" },
    { label: "Patient Capacity", value: "+28%", improvement: "More patients per day" },
    { label: "Clinical Accuracy", value: "99.2%", improvement: "Documentation accuracy" },
    { label: "Staff Satisfaction", value: "+51%", improvement: "Clinician satisfaction scores" }],

    executiveQuote: "This isn't just efficiency—it's giving our clinicians back the time to actually care for patients. The implementation was seamless and the results immediate.",
    executiveName: "Dr. James Chen",
    executiveTitle: "Chief Medical Officer",
    detailedChallenge: "HealthCare NZ clinicians were drowning in administrative burden, spending an average of 3.2 hours per 8-hour shift on documentation. This was leading to burnout, reduced patient satisfaction, and capacity constraints. Previous attempts with generic voice recognition tools failed due to medical terminology accuracy issues and lack of integration with existing systems.",
    detailedSolution: "Mindshift AI implemented AIDoo Analyzer with specialized medical language models, integrated directly into the existing electronic health record system. The solution included real-time voice capture, intelligent clinical note structuring, automated coding suggestions, and compliance checking against medical documentation standards. Crucially, the system learned from each clinician's style while maintaining standardization.",
    implementationTimeline: [
    {
      phase: "Clinical Workflow Analysis",
      duration: "3 weeks",
      activities: [
      "Shadow clinicians across specialties",
      "Document current documentation patterns",
      "Identify integration requirements with EHR",
      "Establish accuracy and compliance baselines"]

    },
    {
      phase: "System Integration",
      duration: "7 weeks",
      activities: [
      "AIDoo Analyzer medical model training",
      "EHR system integration and testing",
      "Medical terminology optimization",
      "Security and HIPAA compliance validation"]

    },
    {
      phase: "Pilot with Early Adopters",
      duration: "5 weeks",
      activities: [
      "Deploy to 15 volunteer clinicians",
      "Gather feedback and refine workflows",
      "Accuracy validation against manual documentation",
      "Build internal champions for broader rollout"]

    },
    {
      phase: "Organization-wide Deployment",
      duration: "5 weeks",
      activities: [
      "Phased rollout by department",
      "Comprehensive clinician training",
      "Ongoing accuracy monitoring",
      "Continuous model refinement"]

    }],

    riskMitigation: [
    "Medical accuracy validated through parallel documentation review for first 90 days",
    "HIPAA compliance maintained with encrypted voice capture and secure data handling",
    "Clinician override capability ensuring human judgment remains paramount",
    "Audit trails for all AI-generated content supporting medical-legal requirements"],

    keyTakeaways: [
    "Clinical AI adoption requires deep understanding of medical workflows and terminology",
    "Early adopter program critical for building organizational confidence",
    "Integration with existing systems non-negotiable for clinical acceptance",
    "Continuous accuracy monitoring and refinement drives long-term success"]

  },
  {
    id: 3,
    company: "RetailCorp Australia",
    industry: "Retail & E-commerce",
    companySize: "Enterprise (8,500 employees)",
    useCase: "Inventory Optimization",
    image: "https://images.unsplash.com/photo-1544365712-91cd4904cd07",
    alt: "Person holding smartphone showing online shopping cart with retail products in modern e-commerce environment",
    challenge: "Excess inventory costing $12M annually with frequent stockouts of high-demand items",
    solution: "Implemented predictive analytics using AIDoo Analyzer across 450 retail locations",
    timeline: "7 months",
    metrics: [
    { label: "Inventory Costs", value: "$8.2M", improvement: "Annual cost reduction" },
    { label: "Stockout Reduction", value: "73%", improvement: "Fewer out-of-stock incidents" },
    { label: "Inventory Turnover", value: "+45%", improvement: "Faster inventory movement" },
    { label: "Revenue Impact", value: "$15M", improvement: "Additional revenue from availability" }],

    executiveQuote: "The predictive accuracy is remarkable. We're now stocking what customers actually want, when they want it, without the massive overhead we used to carry.",
    executiveName: "Michael Thompson",
    executiveTitle: "Chief Supply Chain Officer",
    detailedChallenge: "RetailCorp was trapped in a vicious cycle—overstocking slow-moving items while constantly running out of popular products. Their legacy inventory system couldn't account for regional preferences, seasonal variations, or emerging trends. This resulted in $12M in excess inventory write-downs annually while simultaneously losing $18M in potential sales due to stockouts.",
    detailedSolution: "Mindshift AI deployed AIDoo Analyzer to create a unified predictive inventory platform across all 450 locations. The system analyzes historical sales data, regional trends, weather patterns, social media signals, and competitor activity to generate location-specific stocking recommendations. Crucially, the orchestration layer ensures consistency while allowing for local optimization.",
    implementationTimeline: [
    {
      phase: "Data Integration & Baseline",
      duration: "6 weeks",
      activities: [
      "Consolidate data from 450 locations",
      "Establish current inventory performance metrics",
      "Identify data quality issues and remediation",
      "Define success criteria and KPIs"]

    },
    {
      phase: "Model Development",
      duration: "8 weeks",
      activities: [
      "Build predictive models by category",
      "Incorporate external data sources",
      "Regional variation analysis",
      "Validation against historical performance"]

    },
    {
      phase: "Pilot Deployment",
      duration: "8 weeks",
      activities: [
      "Deploy to 50 representative locations",
      "Monitor prediction accuracy vs. actuals",
      "Refine models based on real-world performance",
      "Train regional managers on system usage"]

    },
    {
      phase: "National Rollout",
      duration: "8 weeks",
      activities: [
      "Phased deployment to all locations",
      "Integration with procurement systems",
      "Ongoing model refinement",
      "Performance monitoring and optimization"]

    }],

    riskMitigation: [
    "Parallel operation with existing systems during pilot to prevent stockout risk",
    "Regional manager override capability maintaining human judgment in local decisions",
    "Gradual confidence building through accuracy validation before full automation",
    "Fallback protocols ensuring business continuity if system issues arise"],

    keyTakeaways: [
    "Retail AI success requires integration of multiple data sources beyond historical sales",
    "Regional variation is critical—one-size-fits-all predictions fail in diverse markets",
    "Change management with regional managers essential for adoption and optimization",
    "Continuous model refinement based on actual outcomes drives sustained improvement"]

  },
  {
    id: 4,
    company: "Manufacturing Solutions Ltd",
    industry: "Manufacturing",
    companySize: "Mid-Market (850 employees)",
    useCase: "Quality Control Automation",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_137c3a868-1769200761642.png",
    alt: "Industrial manufacturing facility with robotic arms and automated assembly line showing modern production environment",
    challenge: "Manual quality inspection causing production bottlenecks and inconsistent defect detection",
    solution: "Deployed computer vision AI with Sferal.ai orchestration for automated quality inspection",
    timeline: "4 months",
    metrics: [
    { label: "Inspection Speed", value: "10x", improvement: "Faster than manual inspection" },
    { label: "Defect Detection", value: "99.7%", improvement: "Accuracy rate" },
    { label: "Production Throughput", value: "+38%", improvement: "Overall capacity increase" },
    { label: "Quality Costs", value: "$1.8M", improvement: "Annual savings from reduced waste" }],

    executiveQuote: "We've eliminated the quality bottleneck entirely. Our inspectors now focus on complex cases while AI handles routine checks with superhuman consistency.",
    executiveName: "Patricia Wong",
    executiveTitle: "VP of Operations",
    detailedChallenge: "Manufacturing Solutions faced a critical bottleneck—their manual quality inspection process could only check 15% of production output, creating both capacity constraints and quality risks. Inspectors were fatigued from repetitive tasks, leading to inconsistent defect detection. The company was losing $3.2M annually to quality issues that reached customers and another $2.1M in production delays.",
    detailedSolution: "Mindshift AI implemented a computer vision system using AIDoo Analyzer, orchestrated through Sferal.ai to ensure consistent quality standards across multiple production lines. The system captures high-resolution images of every product, analyzes them against quality specifications, and automatically routes flagged items for human review. The orchestration layer ensures that quality standards remain consistent even as production requirements change.",
    implementationTimeline: [
    {
      phase: "Quality Standard Documentation",
      duration: "3 weeks",
      activities: [
      "Document all quality specifications",
      "Capture examples of defects and acceptable variations",
      "Establish baseline inspection accuracy",
      "Define integration requirements with production systems"]

    },
    {
      phase: "Vision System Training",
      duration: "6 weeks",
      activities: [
      "Collect training dataset of products and defects",
      "Train computer vision models",
      "Validate accuracy against expert inspectors",
      "Optimize for production line speed requirements"]

    },
    {
      phase: "Production Line Integration",
      duration: "5 weeks",
      activities: [
      "Install cameras and lighting at inspection points",
      "Integrate with production control systems",
      "Parallel operation with manual inspection",
      "Calibration and accuracy validation"]

    },
    {
      phase: "Full Automation",
      duration: "4 weeks",
      activities: [
      "Transition to AI-primary inspection",
      "Human inspector focus on flagged items",
      "Continuous model refinement",
      "Performance monitoring and optimization"]

    }],

    riskMitigation: [
    "Parallel operation with manual inspection during transition ensuring no quality degradation",
    "Human review of all AI-flagged items maintaining final quality authority",
    "Continuous accuracy monitoring with automatic alerts if performance degrades",
    "Fallback to manual inspection capability if system issues occur"],

    keyTakeaways: [
    "Computer vision AI can exceed human consistency in repetitive inspection tasks",
    "Proper lighting and image capture setup critical for accuracy",
    "Human inspectors become more valuable focusing on complex cases and continuous improvement",
    "Continuous model training with new defect types ensures sustained accuracy"]

  }];


  const industries = [
  { value: 'all', label: 'All Industries' },
  { value: 'financial-services', label: 'Financial Services' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail & E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' }];


  const companySizes = [
  { value: 'all', label: 'All Company Sizes' },
  { value: 'mid-market', label: 'Mid-Market (500-2,000)' },
  { value: 'enterprise', label: 'Enterprise (2,000+)' }];


  const useCases = [
  { value: 'all', label: 'All Use Cases' },
  { value: 'customer-service', label: 'Customer Service' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'inventory', label: 'Inventory Management' },
  { value: 'quality-control', label: 'Quality Control' }];


  const outcomes = [
  { value: 'all', label: 'All Outcome Types' },
  { value: 'cost-reduction', label: 'Cost Reduction' },
  { value: 'efficiency', label: 'Efficiency Gains' },
  { value: 'revenue', label: 'Revenue Growth' },
  { value: 'compliance', label: 'Compliance & Risk' }];


  const filteredCaseStudies = caseStudies.filter((study) => {
    const industryMatch = selectedIndustry === 'all' || study.industry.toLowerCase().replace(/\s+/g, '-').includes(selectedIndustry);

    const sizeMatch = selectedCompanySize === 'all' ||
    selectedCompanySize === 'mid-market' && study.companySize.includes('Mid-Market') ||
    selectedCompanySize === 'enterprise' && study.companySize.includes('Enterprise');

    const useCaseMatch = selectedUseCase === 'all' || study.useCase.toLowerCase().replace(/\s+/g, '-').includes(selectedUseCase);

    return industryMatch && sizeMatch && useCaseMatch;
  });

  const handleViewDetails = (id: number) => {
    const study = caseStudies.find((cs) => cs.id === id);
    if (study) {
      setSelectedCaseStudy(study);
    }
  };

  const handleCloseModal = () => {
    setSelectedCaseStudy(null);
  };

  const handleResetFilters = () => {
    setSelectedIndustry('all');
    setSelectedCompanySize('all');
    setSelectedUseCase('all');
    setSelectedOutcome('all');
  };

  if (!isHydrated) {
    return (
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="animate-pulse space-y-8">
              <div className="h-32 bg-muted rounded-lg"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="h-96 bg-muted rounded-lg"></div>
                <div className="h-96 bg-muted rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <>
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <FilterBar
              industries={industries}
              companySizes={companySizes}
              useCases={useCases}
              outcomes={outcomes}
              selectedIndustry={selectedIndustry}
              selectedCompanySize={selectedCompanySize}
              selectedUseCase={selectedUseCase}
              selectedOutcome={selectedOutcome}
              onIndustryChange={setSelectedIndustry}
              onCompanySizeChange={setSelectedCompanySize}
              onUseCaseChange={setSelectedUseCase}
              onOutcomeChange={setSelectedOutcome}
              onReset={handleResetFilters} />


            {filteredCaseStudies.length > 0 ?
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
                {filteredCaseStudies.map((caseStudy) =>
              <CaseStudyCard
                key={caseStudy.id}
                caseStudy={caseStudy}
                onViewDetails={handleViewDetails} />

              )}
              </div> :

            <div className="text-center py-16">
                <div className="text-muted-foreground mb-4">
                  No case studies match your current filters
                </div>
                <button
                onClick={handleResetFilters}
                className="text-primary hover:text-primary/80 transition-colors duration-200">

                  Reset Filters
                </button>
              </div>
            }

            <ROICalculator className="mb-16" />
          </div>
        </div>
      </section>

      <CaseStudyModal
        caseStudy={selectedCaseStudy}
        onClose={handleCloseModal} />

    </>);

};

export default ResultsInteractive;